import { Auth, CognitoHostedUIIdentityProvider } from "@aws-amplify/auth";
import { useCountDown, useMemoizedFn } from "ahooks";

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { LoadingButton } from "@mui/lab";
import { Box, Button, Paper, TextField, Typography } from "@mui/material";
import KeyIcon from "@mui/icons-material/Key";

import { authToken } from "@/api/account";
import useCheckUserStatus from "@/hooks/useCheckUserStatus";
import useNavigateLogin from "@/hooks/useNavigateLogin";
import useSnackbar from "@/hooks/useSnackbar";
import useUserInfo from "@/hooks/useUserInfo";

import googleLogo from "../images/google_log.png";
import useSignInStore from "../useSignInStore";

function OTPVerify(props: { verifyEmail: string; onSignup: () => void }) {
  const [number, setNumber] = useState("");
  const email = props.verifyEmail;
  const password = Math.random().toString(10) + "Abc#";

  const navigate = useNavigate();

  const [verifyCodeLoading, setVerifyCodeLoading] = useState(false);
  const [sendCodeLoading, setSendCodeLoading] = useState(false);

  const [targetDate, setTargetDate] = useState<number>();

  const [countdown] = useCountDown({
    leftTime: targetDate,
    onEnd: () => {
      setTargetDate(undefined);
    },
  });

  const { getRedirectUrl } = useNavigateLogin();

  const { enqueueSnackbar } = useSnackbar();
  const { userInfoContext } = useUserInfo();
  const { checkUserStatus } = useCheckUserStatus();

  const signInMode = useSignInStore((state) => state.signInMode);

  const setSignInModalVisible = useSignInStore((state) => state.setSignInModalVisible);
  const setViewMode = useSignInStore((state) => state.setViewMode);

  const answerCustomChallenge = useMemoizedFn(async (token = "", email = "") => {
    try {
      const cognitoUser = await Auth.signIn(email);
      await Auth.sendCustomChallengeAnswer(cognitoUser, token);

      const user = await Auth.currentAuthenticatedUser();

      const userInfo = {
        email: user.attributes.email,
        token: user.signInUserSession.idToken.jwtToken,
        profile_img: user.attributes?.picture,
      };

      userInfoContext.setUserInfo(userInfo);

      navigate(getRedirectUrl() || "/", { replace: true });
    } catch (error) {
      enqueueSnackbar(`The token is invalid.`);
      setViewMode("signIn");
    }
  });

  const verifyCode = async (code: string) => {
    try {
      if (!code.trim()) {
        enqueueSnackbar(`code cannot be empty.`);
        return;
      }
      const qsCode = code;

      console.log("Login with email and code");

      setVerifyCodeLoading(true);
      const result = await authToken({
        email,
        code: qsCode,
      });
      const reqToken = result.data.token;
      const token = decodeURIComponent(reqToken);

      await answerCustomChallenge(token, email);
    } catch (error) {
      enqueueSnackbar((error as Error).message + ". Sorry for the inconvenience. You can try to login with password if the issue persists. You can always reset your password to retrieve your account.");
    } finally {
      setVerifyCodeLoading(false);
    }
  };

  async function sendMagicLink() {
    if (!email.trim()) {
      enqueueSnackbar(`Email cannot be empty.`);
      return;
    }

    setSendCodeLoading(true);

    Auth.signIn(email)
      .then((result) => {
        console.log("USER FOUND IN", result);
      })
      .catch((e) => {
        if (e.code === "UserNotFoundException") {
          signUp();
        } else {
          console.log(e.code);
          console.error(e);
        }
      });

    // Get current link
    const response = await fetch("https://api.awardtool.com/auth/", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email: email,
        url: window.location.origin,
        origin_path: getRedirectUrl(),
      }),
    }).catch((err) => {
      alert(`Failed to send magic link: ${err.message}`);
    });

    setSendCodeLoading(false);

    setTargetDate(10 * 1000);

    if (response?.status !== 202) {
      const responseBody = await response?.json();
      console.log(`Failed to send magic link: ${responseBody.message}`);
    }
  }

  async function signIn(email = "") {
    try {
      const cUser = await Auth.signIn(email);

      if (cUser) {
        userInfoContext.setUserInfo({
          email: cUser.attributes.email,
          token: cUser.signInUserSession.accessToken.jwtToken,
          profile_img: cUser.attributes?.picture,
        });
      }

      setSignInModalVisible(false);
    } catch (error) {
      console.error(error);
    }
  }

  const signUp = async () => {
    const result = await Auth.signUp({
      username: number,
      password,
      attributes: {
        email: number,
      },
    }).then(() => signIn());
    return result;
  };

  useEffect(() => {
    checkUserStatus();
  }, [checkUserStatus]);

  return (
    <Paper elevation={4} className="!rounded-lg max-w-sm m-auto">
      <div className="py-[32px] px-[24px]">
        <Typography className="!mb-[16px] text-center !text-2xl !font-bold">
          {signInMode === "signIn" ? "Login" : "Sign Up"}
        </Typography>

        <Button
          fullWidth
          size="large"
          // color="primary"
          variant="outlined"
          startIcon={<img src={googleLogo} className="w-[18px]" />}
          onClick={() => Auth.federatedSignIn({ provider: CognitoHostedUIIdentityProvider.Google })}
        >
          Login with Google
        </Button>
        <Button fullWidth size="large" variant="contained" className="!mt-[8px]" onClick={() => navigate("/password")}>
          <KeyIcon className="!text-[20px] mr-1" />
          Login with Password
        </Button>

        <div className="flex my-[8px] items-center justify-center px-[10px]">
          <Box
            className="flex flex-1 h-[1px]"
            sx={{ backgroundColor: (theme) => theme.palette.divider }}
          />
          <Typography variant="body2" className="px-[12px] !text-neutral-400">
            or
          </Typography>
          <Box
            className="flex flex-1 h-[1px]"
            sx={{ backgroundColor: (theme) => theme.palette.divider }}
          />
        </div>
        <Typography className="!mb-[16px] text-center !text-[14px] !font-semihold">
          Verify code from {email} or click the link in the email
        </Typography>

        <div className="flex justify-center">
          <div className="flex gap-1 mx-auto">
            {[0, 1, 2, 3].map((index) => (
              <TextField
                key={index}
                variant="outlined"
                placeholder=""
                inputProps={{
                  maxLength: 1,
                  autoComplete: "off",
                  style: { textAlign: "center" },
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                }}
                InputProps={{ style: { height: "46px", width: "46px" } }}
                value={number[index] || ""}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  const value = event.target.value;
                  if (value && !/^[0-9]$/.test(value)) return;

                  const newCode = number.split("");
                  newCode[index] = value;
                  setNumber(newCode.join(""));

                  // Auto-focus next input after entering a digit
                  if (value && index < 3) {
                    const nextInput =
                      event.target.parentElement?.parentElement?.nextElementSibling?.querySelector(
                        "input"
                      );
                    if (nextInput) nextInput.focus();
                  }
                }}
                onKeyDown={(event) => {
                  // Handle backspace to move to previous input
                  if (event.key === "Backspace" && !number[index] && index > 0) {
                    const prevInput =
                      event.currentTarget.parentElement?.parentElement?.previousElementSibling?.querySelector(
                        "input"
                      );
                    if (prevInput) prevInput.focus();
                  }
                }}
              />
            ))}
          </div>
        </div>

        <LoadingButton
          fullWidth
          size="large"
          color="primary"
          variant="contained"
          className="!mt-[16px]"
          // startIcon={<VpnKeyIcon className="!text-[20px]" />}
          onClick={() => verifyCode(number)}
          loading={verifyCodeLoading}
        >
          Verify Code
        </LoadingButton>

        <LoadingButton
          fullWidth
          size="large"
          color="primary"
          variant="outlined"
          className="!mt-[16px]"
          // startIcon={<VpnKeyIcon className="!text-[20px]" />}
          onClick={() => sendMagicLink()}
          disabled={countdown !== 0}
          loading={sendCodeLoading}
        >
          {countdown > 0 ? `Resend Code(${Math.round(countdown / 1000)}s)` : `Resend Code`}
        </LoadingButton>

        <div className="flex mt-[16px] items-center justify-center">
          <Typography variant="body2">{"Don't have an account?"}</Typography>
          <Button
            variant="text"
            onClick={() => props.onSignup()}
            color="primary"
            sx={{ "&:hover": { backgroundColor: "transparent" }, minWidth: 0, marginLeft: 0 }}
          >
            Sign Up
          </Button>
        </div>

        <Typography variant="body2" textAlign="center" className="pt-[16px]">
          By signing up, you agree to the{` `}
          <Typography
            variant="body2"
            component="a"
            href="/"
            className="no-underline text-inherit"
            sx={{ color: (theme) => theme.palette.primary.main, display: "inline" }}
          >
            Terms of service
          </Typography>
          {` `}and{` `}
          <Typography
            variant="body2"
            component="a"
            href="/privacy-policy"
            className="no-underline text-inherit"
            sx={{ color: (theme) => theme.palette.primary.main, display: "inline" }}
          >
            Privacy Policy
          </Typography>
        </Typography>
      </div>
    </Paper>
  );
}

export default OTPVerify;
